import { apiClient, type ApiResponse } from './client'

export type ReviewNoteStatus = 'EMPTY' | 'GENERATED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED'
export type ReviewNoteSourceMode = 'ALL' | 'ESSENTIAL' | 'WRONG'
export type ReviewNoteItemSource = 'SEEDED' | 'ADDED'

export interface ReviewNoteItemView {
  itemId: number
  quizId: string
  source: ReviewNoteItemSource
  sortOrder: number
  answer: string | null
  gradedIsCorrect: boolean | null
  corrects?: (boolean | null)[] | null
  questionSnapshot: Record<string, unknown> | null
  correctAnswer: unknown[] | null
  explanation: string | null
}

export interface ReviewNoteSourceLessonView {
  lessonId: number
  lessonTitle: string
  /** The source catalog is authoritative; historical saved scopes may omit this field. */
  selectable?: boolean
  curriculumId: number | null
  curriculumTitle: string | null
  courseId: number | null
  courseTitle: string | null
  chapterId: number | null
  chapterTitle: string | null
}

export interface ReviewNotePreviewView {
  sourceLessons: ReviewNoteSourceLessonView[]
  counts: Record<ReviewNoteSourceMode, number>
}

export interface ReviewNotePreviewRequest {
  learningAttemptId?: number | null
  sourceLessonIds?: number[]
}

export interface ReviewNoteView {
  setId: number
  lessonId: number
  learningAttemptId: number | null
  versionNo: number
  sourceMode: ReviewNoteSourceMode
  sourceLessons?: ReviewNoteSourceLessonView[]
  status: ReviewNoteStatus
  requiredForCompletion: boolean
  score: number | null
  printedAt: string | null
  completedAt: string | null
  gradedCount: number
  totalCount: number
  canEditItems: boolean
  canGrade: boolean
  canCancel: boolean
  items: ReviewNoteItemView[]
}

export interface CreateReviewNoteRequest {
  sourceMode: ReviewNoteSourceMode
  learningAttemptId?: number | null
  sourceLessonIds?: number[]
}

export interface CancelReviewNoteRequest {
  reason: string
}

export interface UpdateReviewNoteItemsRequest {
  addQuizIds?: string[]
  removeItemIds?: number[]
}

export interface GradeReviewNoteItemRequest {
  answer?: string | null
  isCorrect?: boolean | null
  manualGrade?: boolean
}

export interface ReviewNotePoolItemView {
  quizId: string
  title: string | null
  alreadyInSet: boolean
}

export interface ReviewNotePrintQuizIdsView {
  quizIds: string[]
}

export interface ReviewNoteSourceAvailabilityView {
  wrongCount: number
}

export interface ReviewNoteScopeView {
  sets: ReviewNoteView[]
  creationClosed: boolean
}

const learnerPath = (learnerId: number) => `/lc/admin/coaching/learners/${learnerId}`

export const lcReviewNoteApi = {
  getScopeSummary: async (
    learnerId: number,
    lessonId: number,
    learningAttemptId: number | null,
  ): Promise<ReviewNoteView | null> => {
    const response = await apiClient.get<ApiResponse<ReviewNoteView | null>>(
      `${learnerPath(learnerId)}/lessons/${lessonId}/review-note`,
      { params: learningAttemptId == null ? undefined : { learningAttemptId } },
    )
    return response.data.data ?? null
  },

  getSourceLessons: async (
    learnerId: number,
    lessonId: number,
    learningAttemptId: number | null,
  ): Promise<ReviewNoteSourceLessonView[]> => {
    const response = await apiClient.get<ApiResponse<ReviewNoteSourceLessonView[]>>(
      `${learnerPath(learnerId)}/lessons/${lessonId}/review-note/source-lessons`,
      { params: learningAttemptId == null ? undefined : { learningAttemptId } },
    )
    return response.data.data
  },

  preview: async (
    learnerId: number,
    lessonId: number,
    request: ReviewNotePreviewRequest,
  ): Promise<ReviewNotePreviewView> => {
    const response = await apiClient.post<ApiResponse<ReviewNotePreviewView>>(
      `${learnerPath(learnerId)}/lessons/${lessonId}/review-note/preview`,
      request,
    )
    return response.data.data
  },

  getSourceAvailability: async (
    learnerId: number,
    lessonId: number,
    learningAttemptId: number | null,
  ): Promise<ReviewNoteSourceAvailabilityView> => {
    const response = await apiClient.get<ApiResponse<ReviewNoteSourceAvailabilityView>>(
      `${learnerPath(learnerId)}/lessons/${lessonId}/review-note/source-availability`,
      { params: learningAttemptId == null ? undefined : { learningAttemptId } },
    )
    return response.data.data
  },

  getScope: async (
    learnerId: number,
    lessonId: number,
    learningAttemptId: number | null,
  ): Promise<ReviewNoteScopeView> => {
    const response = await apiClient.get<ApiResponse<ReviewNoteScopeView>>(
      `${learnerPath(learnerId)}/lessons/${lessonId}/review-notes`,
      { params: learningAttemptId == null ? undefined : { learningAttemptId } },
    )
    return response.data.data
  },

  create: async (
    learnerId: number,
    lessonId: number,
    request: CreateReviewNoteRequest,
  ): Promise<ReviewNoteView> => {
    const response = await apiClient.post<ApiResponse<ReviewNoteView>>(
      `${learnerPath(learnerId)}/lessons/${lessonId}/review-note`,
      request,
    )
    return response.data.data
  },

  getDetail: async (learnerId: number, setId: number): Promise<ReviewNoteView> => {
    const response = await apiClient.get<ApiResponse<ReviewNoteView>>(
      `${learnerPath(learnerId)}/review-notes/${setId}`,
    )
    return response.data.data
  },

  updateItems: async (
    learnerId: number,
    setId: number,
    request: UpdateReviewNoteItemsRequest,
  ): Promise<ReviewNoteView> => {
    const response = await apiClient.put<ApiResponse<ReviewNoteView>>(
      `${learnerPath(learnerId)}/review-notes/${setId}/items`,
      request,
    )
    return response.data.data
  },

  getPool: async (learnerId: number, setId: number): Promise<ReviewNotePoolItemView[]> => {
    const response = await apiClient.get<ApiResponse<ReviewNotePoolItemView[]>>(
      `${learnerPath(learnerId)}/review-notes/${setId}/pool`,
    )
    return response.data.data
  },

  gradeItem: async (
    learnerId: number,
    setId: number,
    quizId: string,
    request: GradeReviewNoteItemRequest,
  ): Promise<ReviewNoteView> => {
    const response = await apiClient.post<ApiResponse<ReviewNoteView>>(
      `${learnerPath(learnerId)}/review-notes/${setId}/items/${quizId}/grade`,
      request,
    )
    return response.data.data
  },

  complete: async (learnerId: number, setId: number): Promise<ReviewNoteView> => {
    const response = await apiClient.post<ApiResponse<ReviewNoteView>>(
      `${learnerPath(learnerId)}/review-notes/${setId}/complete`,
    )
    return response.data.data
  },

  cancel: async (
    learnerId: number,
    setId: number,
    request: CancelReviewNoteRequest,
  ): Promise<ReviewNoteView> => {
    const response = await apiClient.post<ApiResponse<ReviewNoteView>>(
      `${learnerPath(learnerId)}/review-notes/${setId}/cancel`,
      request,
    )
    return response.data.data
  },

  lockForPrint: async (learnerId: number, setId: number): Promise<ReviewNoteView> => {
    const response = await apiClient.post<ApiResponse<ReviewNoteView>>(
      `${learnerPath(learnerId)}/review-notes/${setId}/print-lock`,
    )
    return response.data.data
  },

  getPrintQuizIds: async (
    learnerId: number,
    setId: number,
  ): Promise<ReviewNotePrintQuizIdsView> => {
    const response = await apiClient.get<ApiResponse<ReviewNotePrintQuizIdsView>>(
      `${learnerPath(learnerId)}/review-notes/${setId}/print-quizzes`,
    )
    return response.data.data
  },
}
